import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { errMsg } from "./apiError";
import ChatEquipe from "./ChatEquipe";
import { Pencil, Globe, EyeOff, ArrowRightLeft, X, Check, Trash2, Copy, UserMinus, MessageCircle, ImageDown, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SLOTS = ["GK", "DG", "DC", "DC", "DD", "6", "8", "8", "AG", "AD", "9"];
export const SLOT_LABELS = {
  GK: "Gardien", DG: "Latéral gauche", DC: "Déf. central", DD: "Latéral droit",
  "6": "Milieu déf. (6)", "8": "Milieu (8)", AG: "Ailier gauche", AD: "Ailier droit", "9": "Attaquant (9)",
};

export function SlotGrid({ team }) {
  const bySlot = {};
  team.joueurs.forEach((j) => { (bySlot[j.slot] = bySlot[j.slot] || []).push(j); });
  const counts = {};
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
      {SLOTS.map((slot, i) => {
        counts[slot] = counts[slot] || 0;
        const j = (bySlot[slot] || [])[counts[slot]];
        counts[slot]++;
        return (
          <div key={i} className="flex justify-between items-center border border-[#27272A] px-4 py-2.5 text-sm">
            <span className="text-[#71717A] text-[10px] uppercase tracking-widest">{SLOT_LABELS[slot]}</span>
            <span className={j ? "text-white" : "text-[#3f3f46]"}>{j ? `${j.prenom} ${j.nom}` : "Libre"}</span>
          </div>
        );
      })}
    </div>
  );
}

const inputCls = "bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]";

function TeamEditor({ team, onSaved, onClose }) {
  const [nom, setNom] = useState(team.nom);
  const [coach, setCoach] = useState(team.coach || "");
  const [creneau, setCreneau] = useState(team.creneau || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/teams/${team.id}`, { nom, coach, creneau }, { withCredentials: true });
      toast.success("Équipe mise à jour");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 border border-[#C5A059]/30 bg-[#0A0A0C] p-5" data-testid={`team-editor-${team.numero}`}>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Nom de l'équipe</label>
          <input value={nom} onChange={(e) => setNom(e.target.value)} data-testid={`team-edit-nom-${team.numero}`} className={`${inputCls} w-full mt-1`} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Nom du coach</label>
          <input value={coach} onChange={(e) => setCoach(e.target.value)} data-testid={`team-edit-coach-${team.numero}`} className={`${inputCls} w-full mt-1`} placeholder="Coach …" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Créneau d'entraînement</label>
          <input value={creneau} onChange={(e) => setCreneau(e.target.value)} data-testid={`team-edit-creneau-${team.numero}`} className={`${inputCls} w-full mt-1`} placeholder="Samedi — 10h30" />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={save} disabled={saving} data-testid={`team-edit-save-${team.numero}`} className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
          <Check size={13} /> Enregistrer
        </button>
        <button onClick={onClose} className="text-[#71717A] hover:text-white transition-colors p-1.5" aria-label="Fermer"><X size={15} /></button>
      </div>
    </div>
  );
}

export default function AdminTeams() {
  const [teams, setTeams] = useState([]);
  const [detections, setDetections] = useState([]);
  const [reservistes, setReservistes] = useState([]);
  const [filtre, setFiltre] = useState("");
  const [editing, setEditing] = useState(null);
  const [moveSel, setMoveSel] = useState({});
  const [chatTeam, setChatTeam] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/teams`, { withCredentials: true }).then((r) => setTeams(r.data)).catch(() => {});
    axios.get(`${API}/detections`).then((r) => setDetections(r.data)).catch(() => {});
    axios.get(`${API}/admin/reservistes`, { withCredentials: true }).then((r) => setReservistes(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const detNom = (id) => detections.find((d) => d.id === id)?.nom || id;
  const shown = filtre ? teams.filter((t) => (t.detection_id || "kinshasa-2026") === filtre) : teams;

  const publier = async (team, action) => {
    try {
      await axios.post(`${API}/admin/teams/${team.id}/${action}`, {}, { withCredentials: true });
      toast.success(action === "publier" ? `${team.nom} est maintenant visible sur le site` : `${team.nom} retirée du site`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Erreur"));
    }
  };

  const deplacer = async (team, j) => {
    const key = `${team.id}:${j.registration_id}`;
    const target = moveSel[key];
    if (!target) return;
    try {
      const { data } = await axios.post(`${API}/admin/teams/${team.id}/deplacer`, { registration_id: j.registration_id, target_team_id: target }, { withCredentials: true });
      toast.success(data.message);
      setMoveSel((m) => ({ ...m, [key]: "" }));
      load();
    } catch (e) {
      toast.error(errMsg(e, "Déplacement impossible"));
    }
  };

  const dupliquer = async (team, j) => {
    const key = `${team.id}:${j.registration_id}`;
    const target = moveSel[key];
    if (!target) return;
    try {
      const { data } = await axios.post(`${API}/admin/teams/${team.id}/dupliquer`, { registration_id: j.registration_id, target_team_id: target }, { withCredentials: true });
      toast.success(data.message);
      if (data.avertissement) toast.warning(data.avertissement, { duration: 8000 });
      setMoveSel((m) => ({ ...m, [key]: "" }));
      load();
    } catch (e) {
      toast.error(errMsg(e, "Duplication impossible"));
    }
  };

  const retirer = async (team, j) => {
    if (!window.confirm(`Retirer ${j.prenom} ${j.nom} de ${team.nom} ? (son inscription est conservée)`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/teams/${team.id}/retirer`, { registration_id: j.registration_id }, { withCredentials: true });
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Retrait impossible"));
    }
  };

  const supprimer = async (team) => {
    if (!window.confirm(`Supprimer l'équipe vide « ${team.nom} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/teams/${team.id}`, { withCredentials: true });
      toast.success("Équipe supprimée");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Suppression impossible"));
    }
  };

  const remplir = async () => {
    if (!window.confirm("Assigner automatiquement tous les joueurs confirmés sans équipe ? (poste principal puis secondaire, débordements en réservistes)")) return;
    try {
      const { data } = await axios.post(`${API}/admin/equipes/remplir`, {}, { withCredentials: true });
      toast.success(`${data.message} — ${data.assignes} joueur(s) placé(s), ${data.reservistes} réserviste(s)`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Remplissage impossible"));
    }
  };

  return (
    <div data-testid="admin-teams" className="space-y-8">
      <p className="text-sm text-[#71717A]">
        Les équipes se forment automatiquement par genre puis par poste (U19/U23 selon l'année de naissance). Dès qu'une équipe est complète (11/11), vous pouvez la <strong className="text-[#C5A059]">publier</strong> — seules les équipes publiées sont visibles sur le site.
      </p>
      {detections.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Détection :</label>
          <select value={filtre} onChange={(e) => setFiltre(e.target.value)} data-testid="teams-filter-detection"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-2.5 text-sm focus:outline-none focus:border-[#C5A059]">
            <option value="">Toutes</option>
            {detections.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
          </select>
          <span className="flex-1" />
          <button
            onClick={remplir}
            data-testid="teams-remplir"
            title="Assigne tous les joueurs confirmés sans équipe (poste principal puis secondaire)"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors"
          >
            <Wand2 size={12} /> Remplir les équipes
          </button>
        </div>
      )}
      {shown.length === 0 && (
        <p className="text-center text-[#71717A] text-sm py-12 border border-[#27272A]">Aucune équipe pour le moment. La première équipe se créera à la première inscription payée.</p>
      )}
      {shown.length > 0 && (
        <div className="flex flex-wrap items-center gap-3" data-testid="teams-compteurs">
          <span className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-sky-400/50 text-sky-300" data-testid="teams-count-masculines">
            Équipes masculines — {shown.filter((t) => (t.genre || "masculin") !== "féminin").length}
          </span>
          <span className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-pink-400/50 text-pink-300" data-testid="teams-count-feminines">
            Équipes féminines — {shown.filter((t) => (t.genre || "masculin") === "féminin").length}
          </span>
        </div>
      )}
      {[
        ["masculines", shown.filter((t) => (t.genre || "masculin") !== "féminin")],
        ["féminines", shown.filter((t) => (t.genre || "masculin") === "féminin")],
      ].map(([genreLabel, liste]) => liste.length > 0 && (
        <div key={genreLabel} className="space-y-8" data-testid={`teams-section-${genreLabel === "masculines" ? "masculines" : "feminines"}`}>
          <p className={`text-xs uppercase tracking-[0.3em] border-b pb-3 ${genreLabel === "masculines" ? "text-sky-300 border-sky-400/30" : "text-pink-300 border-pink-400/30"}`}>
            Équipes {genreLabel} ({liste.length})
          </p>
          {liste.map((team) => {
        const complete = team.joueurs.length >= 11;
        const cibles = teams.filter((t) => t.id !== team.id
          && (t.detection_id || "kinshasa-2026") === (team.detection_id || "kinshasa-2026")
          && (t.genre || "masculin") === (team.genre || "masculin")
          && (t.categorie || "U23") === (team.categorie || "U23"));
        return (
          <div key={team.id} data-testid={`team-card-${team.numero}`} className="border border-[#27272A] bg-[#121214] p-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-2xl uppercase font-medium">{team.nom}</h3>
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-1 border ${(team.genre || "masculin") === "féminin" ? "border-pink-400/50 text-pink-300" : "border-sky-400/50 text-sky-300"}`} data-testid={`team-genre-${team.numero}`}>
                    {(team.genre || "masculin") === "féminin" ? "Féminin" : "Masculin"}
                  </span>
                  <span className="text-[9px] uppercase tracking-wider px-2 py-1 border border-[#C5A059]/40 text-[#C5A059]" data-testid={`team-categorie-${team.numero}`}>{team.categorie || "U23"}</span>
                </div>
                <p className="text-[10px] uppercase tracking-widest text-[#C5A059] mt-1">{detNom(team.detection_id || "kinshasa-2026")}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-[#71717A]">{team.joueurs.length}/11 joueurs</span>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border ${team.statut === "complete" ? "border-[#C5A059] text-[#C5A059]" : "border-[#27272A] text-[#71717A]"}`}>
                  {team.statut === "complete" ? "Complète" : "En formation"}
                </span>
                {team.publiee ? (
                  <>
                    <span className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-emerald-400/50 text-emerald-300" data-testid={`team-published-${team.numero}`}>Publiée</span>
                    <button onClick={() => publier(team, "depublier")} data-testid={`team-unpublish-${team.numero}`} title="Retirer du site" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#27272A] text-[#71717A] hover:text-red-400 hover:border-red-400/50 transition-colors">
                      <EyeOff size={12} /> Dépublier
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => publier(team, "publier")}
                    disabled={!complete}
                    data-testid={`team-publish-${team.numero}`}
                    title={complete ? "Rendre visible sur le site" : "Publication possible uniquement quand les 11 places sont comblées"}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#C5A059]"
                  >
                    <Globe size={12} /> Publier
                  </button>
                )}
                <button onClick={() => setEditing(editing === team.id ? null : team.id)} data-testid={`team-edit-${team.numero}`} title="Renommer / coach / créneau" className={`p-1.5 transition-colors ${editing === team.id ? "text-[#C5A059]" : "text-[#71717A] hover:text-[#C5A059]"}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => setChatTeam(team)} data-testid={`team-chat-${team.numero}`} title="Chat d'équipe — voir, écrire, avertir, bloquer" className="p-1.5 text-[#71717A] hover:text-[#C5A059] transition-colors">
                  <MessageCircle size={15} />
                </button>
                {team.visuel_url && (
                  <a href={team.visuel_url} target="_blank" rel="noopener noreferrer" data-testid={`team-affiche-${team.numero}`} title="Affiche du onze de départ (visuel généré automatiquement)" className="p-1.5 text-[#C5A059] hover:text-[#E5C07B] transition-colors">
                    <ImageDown size={15} />
                  </a>
                )}
                {team.joueurs.length === 0 && (
                  <button onClick={() => supprimer(team)} data-testid={`team-delete-${team.numero}`} title="Supprimer cette équipe vide" className="p-1.5 text-[#71717A] hover:text-red-400 transition-colors">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
            <div className="h-px bg-[#27272A] mt-5 relative">
              <div className="absolute inset-y-0 left-0 bg-[#C5A059]" style={{ width: `${(team.joueurs.length / 11) * 100}%` }} />
            </div>
            {editing === team.id && <TeamEditor team={team} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
            <SlotGrid team={team} />
            {team.joueurs.length > 0 && (
              <div className="mt-6 space-y-2" data-testid={`team-roster-admin-${team.numero}`}>
                <p className="text-[10px] uppercase tracking-widest text-[#71717A] mb-3 flex items-center gap-2"><ArrowRightLeft size={11} /> Déplacer ou dupliquer un joueur vers une autre équipe ({team.categorie || "U23"} · {(team.genre || "masculin") === "féminin" ? "F" : "M"}) — la duplication signale tout match où le joueur serait des deux côtés</p>
                {team.joueurs.map((j) => {
                  const key = `${team.id}:${j.registration_id}`;
                  return (
                    <div key={j.registration_id} className="flex flex-wrap items-center gap-3 text-sm border-b border-[#27272A] pb-2">
                      <span className="text-white">{j.prenom} {j.nom}</span>
                      <span className="text-[#71717A] text-xs">{SLOT_LABELS[j.slot]}</span>
                      <span className="flex-1" />
                      <select
                        value={moveSel[key] || ""}
                        onChange={(e) => setMoveSel({ ...moveSel, [key]: e.target.value })}
                        data-testid={`move-select-${j.registration_id.slice(0, 8)}`}
                        className="bg-[#0A0A0C] border border-[#27272A] px-3 py-1.5 text-xs focus:outline-none focus:border-[#C5A059]"
                      >
                        <option value="" disabled>Équipe cible…</option>
                        {cibles.map((t) => <option key={t.id} value={t.id}>{t.nom} ({t.joueurs.length}/11)</option>)}
                      </select>
                      <button
                        onClick={() => deplacer(team, j)}
                        disabled={!moveSel[key]}
                        data-testid={`move-btn-${j.registration_id.slice(0, 8)}`}
                        className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Déplacer
                      </button>
                      <button
                        onClick={() => dupliquer(team, j)}
                        disabled={!moveSel[key]}
                        data-testid={`duplicate-btn-${j.registration_id.slice(0, 8)}`}
                        title="Ajouter aussi dans l'équipe cible (sans le retirer de celle-ci)"
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-sky-400/50 text-sky-300 hover:bg-sky-400 hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Copy size={11} /> Dupliquer
                      </button>
                      <button
                        onClick={() => retirer(team, j)}
                        data-testid={`remove-btn-${j.registration_id.slice(0, 8)}`}
                        title="Retirer de cette équipe (inscription conservée)"
                        className="p-1.5 text-[#71717A] hover:text-red-400 transition-colors"
                      >
                        <UserMinus size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {team.match_prevu && (
              <p className="mt-6 text-sm text-white">Match prévu : <span className="text-[#C5A059]">{team.match_prevu}</span></p>
            )}
            {team.notification && (
              <p className="mt-2 text-[#C5A059] text-sm font-editorial italic">"{team.notification}"</p>
            )}
            <a href={`/equipes/${team.id}`} data-testid={`admin-team-page-${team.numero}`} className="inline-block mt-5 text-[#C5A059] text-xs uppercase tracking-[0.25em] border-b border-[#C5A059]/40 pb-1 hover:border-[#C5A059] transition-colors">
              Voir la page publique
            </a>
            <p className="mt-4 text-xs text-[#71717A]">Coach : {team.coach || "À désigner"} · Créneau : {team.creneau || "À confirmer"}</p>
            {/* Réservistes du même genre, groupés par poste */}
            {(() => {
              const pool = reservistes.filter((r) => (r.genre || "masculin") === (team.genre || "masculin"));
              if (!pool.length) return null;
              const parPoste = {};
              for (const r of pool) (parPoste[r.poste_principal] = parPoste[r.poste_principal] || []).push(`${r.prenom} ${r.nom}`);
              return (
                <div className="mt-3 border-t border-[#27272A] pt-3" data-testid={`team-reservistes-${team.numero}`}>
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-2">Réservistes disponibles (remplacement possible pour chaque match)</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                    {Object.entries(parPoste).map(([poste, noms]) => (
                      <p key={poste} className="text-xs text-[#A1A1AA]"><span className="text-[#C5A059] font-semibold">{poste}</span> — {noms.join(", ")}</p>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        );
          })}
        </div>
      ))}
      {chatTeam && <ChatEquipe team={chatTeam} onClose={() => setChatTeam(null)} />}
    </div>
  );
}
